import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ChapterHeading, Reveal } from "./Reveal";
import { IMAGES } from "../config/site";

const FACTS = [
  ["Address", "728 Ala Moana Boulevard, Honolulu, Hawai'i 96813"],
  ["Neighborhood", "Our Kaka'ako Master Plan community — front row on Ala Moana Boulevard"],
  ["Residences", "285 residences in a 39-story tower"],
  ["Interiors", "Olson Kundig interiors; 9' floor-to-ceiling windows, Miele appliances, TOTO fixtures"],
  ["Pricing", "1-bedrooms from $890,000 · 2-bedrooms from $1,250,000 · 3-bedrooms from $2,701,000"],
  ["Setting", "At the bend of Ala Moana Boulevard with views of Diamond Head, mountains, and the ocean — across the street from SALT, close to Ala Moana Center"],
];

const Project = () => {
  const imgRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: imgRef, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section className="py-24 md:py-32 bg-sand" data-testid="project-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <ChapterHeading number="02" overline="The Project" title="A boutique tower at the bend of Ala Moana Boulevard." />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mt-14 md:mt-20 items-start">
          <Reveal className="lg:col-span-7">
            <div ref={imgRef} className="relative overflow-hidden aspect-[4/5] md:aspect-[5/4]" data-testid="project-image-frame">
              <motion.img
                src={IMAGES.tower}
                alt="High-rise residences catching golden hour light — placeholder imagery"
                className="absolute inset-0 w-full h-[120%] object-cover"
                style={{ y }}
                data-testid="project-image"
              />
            </div>
            <p className="mt-3 text-[10px] tracking-[0.2em] uppercase text-stone2">Atmospheric placeholder — not a developer rendering</p>
          </Reveal>

          <div className="lg:col-span-5">
            {FACTS.map(([k, v], i) => (
              <Reveal key={k} delay={i * 0.08}>
                <div className="border-b border-line py-5 first:pt-0" data-testid={`project-fact-${i}`}>
                  <p className="text-[10px] tracking-[0.25em] uppercase font-semibold text-teal">{k}</p>
                  <p className="font-serif text-lg md:text-xl font-light text-charcoal mt-2 leading-snug">{v}</p>
                </div>
              </Reveal>
            ))}
            <Reveal delay={0.4}>
              <p className="mt-8 text-xs text-stone2 leading-relaxed italic">
                Details from the developer&rsquo;s public filings and official website; subject to change. All prices are approximate starting figures published by the developer.
              </p>
              <a href="https://kaliualamoana.com/" target="_blank" rel="noopener noreferrer" className="inline-block mt-6 text-xs tracking-[0.2em] uppercase font-semibold text-teal border-b border-teal pb-1 hover:text-teal-deep hover:border-teal-deep transition-colors" data-testid="project-official-link">
                View the official project website &rarr;
              </a>
              <p className="mt-6 text-xs text-stone2 leading-relaxed italic" data-testid="project-framing-line">
                Browse the official site, then let&rsquo;s talk about what it doesn&rsquo;t tell you.
              </p>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Project;
